/**
 * OrivonAI CTA & Navigation Design System
 * Five distinct interaction types — never swap one for another.
 */

/** Must match `DashboardNav` aside width — page grids column 1 so main content clears the nav (was 200px, caused 20px overlap). */
export const DASHBOARD_NAV_WIDTH_PX = 220;

/** Breakpoint for mobile app layout: single column + menu drawer. Align with `ChapterDemo` / `DemoCycleDemoLayout`. */
export const LAYOUT_MOBILE_MAX_PX = 768;
export const layoutMobileMediaQuery = `(max-width: ${LAYOUT_MOBILE_MAX_PX}px)`;

/**
 * Wider demo product shell: hamburger, full-width main, recs as FAB.
 * Use for `/demo/*` and any layout that shows `NovaSidebarWithCollapse`.
 */
export const DEMO_SHELL_MOBILE_MAX_PX = 900;
export const demoShellMobileMediaQuery = `(max-width: ${DEMO_SHELL_MOBILE_MAX_PX}px)`;

/** localStorage: "1" = recommendations rail expanded, unset/false = collapsed (default for new visitors). */
export const DEMO_RECS_OPEN_LS_KEY = 'oai_demo_recs_open';

/** Pinned strip when recs are collapsed (desktop). */
export const DEMO_RECS_STRIP_WIDTH_PX = 48;
/** Main column inset so text doesn’t sit under the strip. */
export const DEMO_RECS_MAIN_INSET_PX = 64;
/** Demo shell main column is intentionally uncapped on desktop. */
export const DEMO_SHELL_MAIN_MAX_WIDTH_PX = null;

/**
 * Vertical gap between stacked demo sections (How it works / chapter demos).
 * Matches Journey My Journey — e.g. Nova check-in bar → My story.
 */
export const DEMO_SECTION_STACK_GAP_PX = 20;

/** Main scroll area padding for `/demo/*` chapter shells — matches Journey `main` and ChapterDemo. */
export const DEMO_PRODUCT_MAIN_PADDING_PX = 24;
/** Slightly tighter horizontal padding on small screens; keeps copy readable without wasted width. */
export const DEMO_PRODUCT_MAIN_PADDING_PX_MOBILE = 16;

/**
 * Main column padding for ChapterDemo / profile demo `main` — use with `m` from `useMediaQuery(layoutMobileMediaQuery)`.
 */
export function demoMainColumnPadding(m) {
  if (!m) return DEMO_PRODUCT_MAIN_PADDING_PX;
  const h = 'max(16px, env(safe-area-inset-left, 0px))';
  const r = 'max(16px, env(safe-area-inset-right, 0px))';
  return `${DEMO_PRODUCT_MAIN_PADDING_PX_MOBILE}px ${r} ${DEMO_PRODUCT_MAIN_PADDING_PX_MOBILE}px ${h}`;
}

/** `/demo/*` main padding when the 900px shell is active: 16px vertical, 20px horizontal + safe area. */
export function demoShellMainColumnPadding(isNarrow) {
  if (!isNarrow) return `${DEMO_PRODUCT_MAIN_PADDING_PX}px`;
  const t = 'max(16px, env(safe-area-inset-top, 0px))';
  const r = 'max(20px, env(safe-area-inset-right, 0px))';
  const b = '16px';
  const l = 'max(20px, env(safe-area-inset-left, 0px))';
  return `${t} ${r} ${b} ${l}`;
}

/** Chapter demo `main` padding: extra right inset on desktop for the 48px Nova strip. */
export function chapterDemoMainPadding(isNarrow) {
  if (isNarrow) return demoShellMainColumnPadding(true);
  // Keep visible gutters symmetric by accounting for the fixed
  // collapsed Nova Daily strip width on the right edge.
  return `20px ${20 + DEMO_RECS_STRIP_WIDTH_PX}px 20px 20px`;
}

/**
 * Scaled type for product demo screens (Fertility home, TTC, etc.) — most body text was 10–12px on mobile.
 * Use: `const dt = demoType(m);` then `fontSize: dt.small`, etc.
 */
export function demoType(m) {
  if (!m) {
    return {
      micro: 10,
      caption: 11,
      label: 11,
      small: 12,
      link: 12,
      bodySm: 13,
      body: 16,
      h2card: 16,
    };
  }
  return {
    micro: 12,
    caption: 13,
    label: 12,
    small: 14,
    link: 14,
    bodySm: 15,
    body: 17,
    h2card: 17,
  };
}

/** Flex column wrapper: use under ChapterDemo main and for ChapterShell + page body stacks. */
export const demoChapterStackColumnStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: DEMO_SECTION_STACK_GAP_PX,
  width: '100%',
  boxSizing: 'border-box',
};

/**
 * Vertical gap between stacked section-level cards (white bordered cards) under a page body.
 * Matches /demo/fertility/home — e.g. Clinic & appointments → Ovarian Reserve Baseline Panel → Recent Labs.
 * Use inside page content only; keep demoChapterStackColumnStyle (20px) for hero → Nova → body.
 */
export const DEMO_SECTION_CARD_STACK_GAP_PX = 14;

export const demoSectionCardStackStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: DEMO_SECTION_CARD_STACK_GAP_PX,
  width: '100%',
  boxSizing: 'border-box',
};

/**
 * Apply to main demo content wrappers for `/demo/fertility/*` and `/demo/trying-to-conceive` / `/demo/ttc/*`.
 * Stylesheet rules in `index.css` give text fields a shared light mint fill (see `--demo-product-input-fill`).
 */
export const DEMO_PRODUCT_INPUT_SCOPE_CLASS = 'demo-product-input-scope';

/** Gap for 3–4 column stat / key-number grids inside a section card (matches home). */
export const DEMO_MINI_CARD_GRID_GAP_PX = 12;

// TYPE 1: PRIMARY BUTTON — single most important action per section
export const btnPrimary = {
  background: '#D4537E',
  color: 'white',
  border: 'none',
  borderRadius: 999,
  padding: '8px 20px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background 0.12s',
};

export const btnPrimarySmall = {
  ...btnPrimary,
  padding: '5px 14px',
  fontSize: 10,
};

// TYPE 2: SECONDARY / GHOST BUTTON
export const btnGhost = {
  background: 'transparent',
  color: '#D4537E',
  border: '0.5px solid rgba(212,83,126,0.3)',
  borderRadius: 999,
  padding: '6px 14px',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const btnGhostSmall = {
  ...btnGhost,
  padding: '4px 10px',
  fontSize: 10,
};

// TYPE 3: TAB NAVIGATION — underline style
export const tabNavContainer = {
  display: 'flex',
  borderBottom: '0.5px solid var(--color-border-tertiary, #e5e5e5)',
};

export const tabNavItem = (active) => ({
  padding: '10px 16px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  color: active ? '#D4537E' : 'var(--color-text-tertiary, #888)',
  borderBottom: `2px solid ${active ? '#D4537E' : 'transparent'}`,
  marginBottom: -0.5,
  background: 'transparent',
  border: 'none',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
});

/** Same underline pattern as tabNavItem, with a custom accent (e.g. First Chapter violet). */
export const tabNavItemColored = (active, accent = '#D4537E') => ({
  padding: '10px 14px',
  fontSize: 12,
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  color: active ? accent : 'var(--color-text-tertiary, #888)',
  borderBottom: `2px solid ${active ? accent : 'transparent'}`,
  marginBottom: -0.5,
  background: 'transparent',
  border: 'none',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
});

// TYPE 4: FILTER / TOGGLE PILL
export const pillFilter = (active) => ({
  padding: '4px 13px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  background: active ? '#FBEAF0' : 'var(--color-background-secondary, #f5f5f5)',
  color: active ? '#D4537E' : 'var(--color-text-secondary, #666)',
  border: active ? '0.5px solid rgba(212,83,126,0.3)' : '0.5px solid var(--color-border-secondary, #ddd)',
});

export const pillSaved = {
  padding: '4px 13px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  background: '#E1F5EE',
  color: '#0F6E56',
  border: '0.5px solid rgba(29,158,117,0.3)',
};

// TYPE 5: INLINE TEXT LINK
export const linkInline = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 10,
  fontWeight: 500,
  color: '#D4537E',
  cursor: 'pointer',
  textDecoration: 'none',
  fontFamily: 'inherit',
};

export const linkInlineMuted = {
  ...linkInline,
  color: 'var(--color-text-tertiary, #888)',
};

// Message link (blue) — for Community sidebar "Message"
export const linkMessage = {
  ...linkInline,
  color: '#185FA5',
};

/** Playfair page titles — matches Journey `/demo/fertility/myjourney` ("My Journey — Fertility") */
export const PAGE_TITLE_FONT_SERIF = "'Playfair Display', Georgia, serif";

/** Main page H1 on demo chapter screens — matches /my-story (Playfair 24, bold) */
export const pageTitleH1 = {
  fontFamily: PAGE_TITLE_FONT_SERIF,
  fontSize: 24,
  fontWeight: 700,
  color: 'var(--text)',
  marginBottom: 6,
  lineHeight: 1.25,
};

/** One line under page H1 — matches /my-story descriptor (13px muted) */
export const demoFlowPageDescriptor = {
  fontSize: 13,
  color: 'var(--muted)',
  lineHeight: 1.5,
  marginTop: 0,
};

/** Section titles inside long-form demo pages (Journey, etc.) — same weight as My Story H1 */
export const demoFlowSectionTitle = {
  fontFamily: PAGE_TITLE_FONT_SERIF,
  fontSize: 24,
  fontWeight: 700,
  color: 'var(--text)',
  lineHeight: 1.25,
  marginBottom: 6,
};

export const demoFlowSectionDescriptor = {
  fontSize: 13,
  color: 'var(--muted)',
  lineHeight: 1.5,
  marginTop: 0,
  marginBottom: 0,
};

/** Level 2 — hero / status card title (teal-bordered gradient cards), max 18px */
export const introHeadlineSerif = {
  fontFamily: PAGE_TITLE_FONT_SERIF,
  fontSize: 18,
  fontWeight: 700,
  color: 'var(--text)',
  lineHeight: 1.25,
  marginBottom: 4,
};

/** Fertility gradient cards use dark plum body color */
export const introHeadlineSerifDeep = {
  ...introHeadlineSerif,
  color: '#2d1a22',
};

/** Rose keyline + soft shadow for intro / status gradient cards (all chapters) */
export const introCardKeyline = {
  border: '1px solid #D18E9B',
  borderRadius: 22,
  boxShadow: '0 2px 16px rgba(17, 17, 17, 0.06)',
};

/** Thin pink keyline for main section cards on `/demo/fertility/*` — matches home grid cards + saved doctor cards. */
export const FERTILITY_DEMO_SECTION_BORDER = '1px solid rgba(212, 83, 126, 0.35)';

/** Fertility demo — section "Edit" control (My story reference): muted rose on pale pink, thin matching border. */
export const FERTILITY_DEMO_EDIT_ROSE = '#D87B8C';
export const FERTILITY_DEMO_EDIT_BG = '#FDF2F4';

export const fertilityDemoEditButton = {
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  padding: '6px 14px',
  borderRadius: 6,
  background: FERTILITY_DEMO_EDIT_BG,
  color: FERTILITY_DEMO_EDIT_ROSE,
  border: `1px solid ${FERTILITY_DEMO_EDIT_ROSE}`,
  cursor: 'pointer',
  flexShrink: 0,
};

/** Shown when My story / health sections are in edit mode (Save / Done). */
export const fertilityDemoSaveButton = {
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  padding: '6px 14px',
  borderRadius: 8,
  background: 'var(--sage)',
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  flexShrink: 0,
};
